import { useMemo } from "preact/hooks"
import { exactCurrencyFormatter } from "../../../../utils"
import { Promotion } from "../../promo_creation/types"
import { AccrualRow, accrual_totals, use_accrual_rows } from "../accrual_summary_view"
import { PromotionDownloads } from "./promotion_downloads"

interface FilteredSpendSummaryProps {
  /** The promotions the Calendar's filters currently show. */
  promotions: Promotion[]
  year: number
  className?: string
}

/** The accrual rows for exactly the promotions shown, by id. */
export function accrual_rows_for(promotions: { id?: string }[], rows: AccrualRow[]): AccrualRow[] {
  const ids = new Set(promotions.map((promotion: { id?: string }) => promotion.id).filter(Boolean))
  return rows.filter((row: AccrualRow) => ids.has(row.id))
}

/**
 * Planned vs. actual spend for what the Calendar's filters show — e.g. every "All Other" TPR
 * bucket — so a plan can be checked against what was actually deducted without leaving the page.
 */
export function FilteredSpendSummary({ promotions, year, className }: FilteredSpendSummaryProps) {
  const { rows, loading } = use_accrual_rows(year)
  const totals = useMemo(() => accrual_totals(accrual_rows_for(promotions, rows)), [promotions, rows])
  const has_plan = totals.with_plan > 0
  const remaining = totals.latest_estimate - totals.actual

  return (
    <div class={`flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm ${className ?? ""}`}>
      <span class="text-gray-500">
        {promotions.length} promotion{promotions.length === 1 ? "" : "s"} shown in {year}
      </span>
      {loading ? (
        <span class="text-gray-400">Loading spend…</span>
      ) : (
        <>
          <span>
            <span class="text-gray-500">Planned </span>
            <span class="font-semibold text-gray-900">{has_plan ? exactCurrencyFormatter(totals.planned) : "—"}</span>
          </span>
          <span>
            <span class="text-gray-500">Actual </span>
            <span class="font-semibold text-gray-900">{exactCurrencyFormatter(totals.actual)}</span>
          </span>
          {has_plan && (
            <span>
              <span class="text-gray-500">{remaining >= 0 ? "Unspent vs. estimate " : "Over estimate "}</span>
              <span class={`font-semibold ${remaining >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                {exactCurrencyFormatter(Math.abs(remaining))}
              </span>
            </span>
          )}
        </>
      )}
      <PromotionDownloads
        className="ml-auto"
        promotion_ids={promotions.map((promotion: Promotion) => promotion.id).filter((id): id is string => !!id)}
      />
    </div>
  )
}
